'use client';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Badge — status & label chip. Brief §3.11.
 * Adds `photoCount`, `propertyType`, `priceMeter` overlays for property cards.
 * Existing variants kept for backward compatibility (rounded-full pills).
 */
export const badgeVariants = cva(
  'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-medium tracking-wide leading-none',
  {
    variants: {
      variant: {
        default: 'bg-surface-cream text-ink-secondary',
        primary: 'bg-primary/10 text-primary',
        navy: 'bg-primary text-primary-foreground',
        accent: 'bg-accent/12 text-accent-700',
        accentSolid: 'bg-accent text-accent-foreground',
        success: 'bg-success/12 text-success',
        warning: 'bg-warning/12 text-warning',
        danger: 'bg-error/12 text-error',
        destructive: 'bg-error/12 text-error',
        info: 'bg-info/12 text-info',
        secondary: 'bg-surface-cream text-ink-secondary',
        purple: 'bg-purple-100 text-purple-700',
        outline: 'border border-border bg-transparent text-ink',
        glass: 'glass-dark text-surface-light',
        // Real-estate status badges (brief §3.11)
        new: 'rounded-sm bg-cta text-cta-foreground shadow-sm uppercase tracking-wide',
        featured: 'bg-primary text-primary-foreground',
        priceDrop: 'bg-success text-success-foreground',
        verified: 'border border-success/30 bg-success/10 text-success',
        // Photo count overlay (dark semi-transparent pill, bottom-left on card image)
        photoCount: 'bg-primary/70 text-white backdrop-blur-sm',
        // Property type (outlined accent pill)
        propertyType: 'border border-cta/40 bg-transparent text-cta-hover',
        // Inline price-per-m² label
        priceMeter: 'bg-transparent text-ink-secondary px-0 py-0 text-xs',
      },
      size: {
        sm: 'text-[10px] px-2 py-0.5',
        md: 'text-[11px] px-2.5 py-1',
        lg: 'text-xs px-3 py-1.5',
      },
    },
    defaultVariants: { variant: 'default', size: 'md' },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
  VariantProps<typeof badgeVariants> { }

export function Badge({ variant, size, className, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant, size }), className)} {...props} />;
}
